import React, { useEffect, useState } from "react";
import { BriefcaseIcon, CubeIcon, PlusIcon } from "@heroicons/react/outline";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import i18n from "i18next";
import ExpandableDescription from "../../../../Components/v2/Accounting/ExpandableDescription";
import AccountingWrapper from "../../../../Components/v2/Accounting/AccountingWrapper";
import TableActions from "../../../../Components/v2/Table/TableActions";
import Table from "../../../../Components/v2/Table";
import {
  addLocalNotification,
  clearBusinessProduct,
  clearBusinessProductError,
  requestDeleteBusinessProduct,
  requestGetBusinessProduct,
  requestListBusinessProduct,
  useAuthState,
  useBusinessProductDispatch,
  useBusinessProductState,
  useNotificationDispatch,
} from "../../../../Context";
import ProductModal from "../../../../Components/v2/Accounting/ProductModal";
import ProductStockModal from "../../../../Components/v2/Accounting/ProductStockModal";
import ProductWriteDownModal from "../../../../Components/v2/Accounting/ProductWriteDownModal";
import ProductDetailModal from "../../../../Components/v2/Accounting/ProductDetailModal";
import Pagination from "../../../../Components/v2/Pagination";
import { ModalAlert } from "../../../../Components/ModalAlert";
import {
  cancelAPIRequest,
  currencyFormat,
  formatDateTime,
} from "../../../../Utils";
import ErrorDialog from "../../../../Components/v2/ErrorDialog";

const ProductsServices = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const [productModalOpen, setProductModalOpen] = useState(false);
  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [writeDownModalOpen, setWriteDownModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(
    location.state?.product ?? null,
  );
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(5);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const dispatch = useBusinessProductDispatch();
  const notificationDispatch = useNotificationDispatch();

  const { user } = useAuthState();
  const {
    request,
    businessProduct,
    businessProducts,
    businessProductsLoading,
    businessProductsError,
    businessProductNextTokens,
    deleteProductError,
    deleteProductLoading,
    deletedID,
  } = useBusinessProductState();

  useEffect(() => {
    return () => {
      cancelAPIRequest(request);
      clearBusinessProduct(dispatch);
      clearBusinessProductError(dispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open detail modal if navigated from another page with a product in state
  useEffect(() => {
    if (location.state?.product) {
      setDetailModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user?.mainBusiness?.id) {
      setPage(1);
      requestListBusinessProduct(dispatch, {
        page: 1,
        limit: perPage,
        businessID: user.mainBusiness.id,
      });
    }
  }, [dispatch, perPage, user?.mainBusiness?.id]);

  useEffect(() => {
    if (
      businessProduct?.id &&
      selectedProduct?.id &&
      businessProduct?.id === selectedProduct?.id
    ) {
      setSelectedProduct(businessProduct);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessProduct]);

  //  Handle delete success
  useEffect(() => {
    if (deletedID) {
      // Close modal
      setShowConfirmModal(false);
      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `delete-product-${deletedID}`,
        type: "success",
        title: "common.success",
        body: "products.notifications.deleteSuccess",
        showFull: true,
      });
    }
  }, [notificationDispatch, deletedID]);

  useEffect(() => {
    if (deleteProductError) {
      setShowConfirmModal(false);
    }
  }, [deleteProductError]);

  useEffect(() => {
    if (deleteProductError && !showConfirmModal) {
      setShowErrorModal(true);
    }
  }, [deleteProductError, showConfirmModal]);

  const onPageChange = (selectedPage) => {
    setPage(selectedPage);
    if (businessProductNextTokens[selectedPage] || selectedPage === 1) {
      requestListBusinessProduct(dispatch, {
        page: selectedPage,
        limit: perPage,
        businessID: user?.mainBusiness?.id,
        nextTokens: businessProductNextTokens[selectedPage],
      });
    }
  };

  const onPerPageChange = (itemsPerPage) => {
    setPerPage(itemsPerPage);
  };

  const handleEdit = (product) => {
    setSelectedProduct(product);
    setProductModalOpen(true);
  };

  const handleSetStock = (product) => {
    setSelectedProduct(product);
    setStockModalOpen(true);
  };

  const handleWriteDown = (product) => {
    setSelectedProduct(product);
    setWriteDownModalOpen(true);
  };

  const handleDelete = (product) => {
    setSelectedProduct(product);
    setShowConfirmModal(true);
  };

  const onDelete = () => {
    if (selectedProduct?.id) {
      requestDeleteBusinessProduct(dispatch, {
        id: selectedProduct.id,
      });
    }
  };

  const getDeleteDescription = () => {
    if (!selectedProduct) return null;
    const category =
      selectedProduct.category?.name || t("common.uncategorized");
    const price = currencyFormat(selectedProduct.price);
    const hasStock =
      selectedProduct.type === "GOODS" && selectedProduct.currentStock > 0;
    return t(
      hasStock
        ? "products.modal.description.deleteItem"
        : "products.modal.description.deleteItemNoStock",
      {
        name: selectedProduct.name,
        category,
        price,
        stock: selectedProduct.currentStock,
        interpolation: { escapeValue: false },
      },
    );
  };

  const handleLoadProduct = () => {
    if (selectedProduct?.id) {
      requestGetBusinessProduct(dispatch, { id: selectedProduct.id });
    }
  };

  const handleViewDetail = (product) => {
    setSelectedProduct(product);
    setDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedProduct(null);
  };

  const handleEditFromDetail = (product) => {
    setDetailModalOpen(false);
    setSelectedProduct(product);
    setProductModalOpen(true);
  };

  const handleSetStockFromDetail = (product) => {
    setDetailModalOpen(false);
    setSelectedProduct(product);
    setStockModalOpen(true);
  };

  const handleWriteDownFromDetail = (product) => {
    setDetailModalOpen(false);
    setSelectedProduct(product);
    setWriteDownModalOpen(true);
  };

  const handleAdd = () => {
    setSelectedProduct(null);
    setProductModalOpen(true);
  };

  const handleClose = () => {
    cancelAPIRequest(request);
    setProductModalOpen(false);
    setSelectedProduct(null);
  };

  const handleCloseStockModal = () => {
    cancelAPIRequest(request);
    setStockModalOpen(false);
    setSelectedProduct(null);
  };

  const handleCloseWriteDownModal = () => {
    cancelAPIRequest(request);
    setWriteDownModalOpen(false);
    setSelectedProduct(null);
  };

  return (
    <AccountingWrapper>
      <main className="lg:col-span-9">
        {/* Page header */}
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                {t("products.label")}
              </h2>
            </div>
            <div className="mt-4 flex md:mt-0 md:ml-4">
              <button
                onClick={handleAdd}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                {t("products.header.title.add")}
              </button>
            </div>
          </div>
        </div>

        {/* Products Table */}
        <div className="px-4 py-5 sm:px-6 lg:px-8">
          <Table
            isLoading={businessProductsLoading}
            error={businessProductsError}
            onRetry={() => onPageChange(page)}
            columns={[
              {
                header: "products.fields.name",
                sortable: true,
                sortKey: "name",
                accessor: (product) => (
                  <div className="flex items-center text-wrap min-w-96">
                    <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      {product.type === "GOODS" ? (
                        <CubeIcon className="h-6 w-6 text-gray-400" />
                      ) : (
                        <BriefcaseIcon className="h-6 w-6 text-gray-400" />
                      )}
                    </div>
                    <div className="ml-4">
                      <ExpandableDescription
                        text={
                          product.category?.name || t("common.uncategorized")
                        }
                        maxLength={30}
                        textClassName="text-sm font-semibold text-blue-600"
                      />
                      <ExpandableDescription
                        text={product.name}
                        maxLength={50}
                        textClassName="text-sm font-medium text-gray-900"
                      />
                      {product.reference ? (
                        <ExpandableDescription
                          text={product.reference}
                          maxLength={30}
                          textClassName="text-sm text-gray-500"
                        />
                      ) : (
                        <div className="text-sm text-gray-500">-</div>
                      )}
                    </div>
                  </div>
                ),
              },
              {
                header: "products.fields.price",
                sortable: true,
                sortKey: "price",
                accessor: (product) => `Rp${product.price.toLocaleString()}`,
                align: "right",
              },
              {
                header: "products.fields.cost",
                sortable: true,
                sortKey: "cost",
                accessor: (product) =>
                  product.cost ? `Rp${product.cost.toLocaleString()}` : "-",
                align: "right",
              },
              {
                header: "products.fields.averageCost",
                accessor: (product) => {
                  const avg =
                    product.averageCost != null
                      ? product.averageCost
                      : product.cost;
                  return avg != null ? currencyFormat(avg) : "-";
                },
                align: "right",
              },
              {
                header: "products.fields.stock",
                accessor: (product) =>
                  product.type === "SERVICES" ? (
                    <div className="text-sm text-gray-500">-</div>
                  ) : product.currentStock ? (
                    <div className="flex flex-col items-end">
                      <div className="text-sm text-gray-900">
                        {product.currentStock}
                      </div>
                      {product.stockLastUpdatedAt && (
                        // An instant, not a calendar day: two stock movements on the
                        // same day are only tellable apart by the clock. The stock
                        // history screen already shows this field with its time, so
                        // a date-only value here read as a different figure.
                        <div className="text-sm text-gray-500">
                          {formatDateTime(
                            product.stockLastUpdatedAt,
                            i18n.language,
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">
                      {t("products.fields.notSet")}
                    </div>
                  ),
                align: "right",
              },
              {
                header: t("common.actions.label"),
                accessor: (product, index) => (
                  <TableActions
                    position={
                      index === businessProducts.length - 1 ? "top" : "bottom"
                    }
                    actions={[
                      {
                        label: t("common.view"),
                        onClick: () => handleViewDetail(product),
                      },
                      {
                        label: t("common.edit"),
                        onClick: () => handleEdit(product),
                      },
                      ...(product.type === "GOODS"
                        ? [
                            {
                              label: t("products.actions.setStock"),
                              onClick: () => handleSetStock(product),
                            },
                            {
                              label: t("products.actions.writeDown"),
                              onClick: () => handleWriteDown(product),
                            },
                          ]
                        : []),
                      {
                        label: t("common.delete"),
                        onClick: () => handleDelete(product),
                        variant: "danger",
                        disabled: Boolean(product.inUse),
                        tooltip: product.inUse
                          ? t("common.inUseInTransactions")
                          : undefined,
                      },
                    ]}
                  />
                ),
                align: "right",
              },
            ]}
            data={businessProducts}
          />
        </div>

        {/* Pagination */}
        <Pagination
          nextTokens={businessProductNextTokens}
          currentPage={page}
          isLoading={businessProductsLoading}
          onPageChange={onPageChange}
          perPage={perPage}
          onPerPageChange={onPerPageChange}
        />

        {/* Product Modal */}
        {productModalOpen && (
          <ProductModal
            open={productModalOpen}
            onClose={handleClose}
            product={selectedProduct}
          />
        )}

        {/* Detail Modal */}
        {detailModalOpen && (
          <ProductDetailModal
            open={detailModalOpen}
            onClose={handleCloseDetailModal}
            product={selectedProduct}
            onEdit={handleEditFromDetail}
            onSetStock={handleSetStockFromDetail}
            onWriteDown={handleWriteDownFromDetail}
          />
        )}

        {/* Stock Modal */}
        {stockModalOpen && (
          <ProductStockModal
            open={stockModalOpen}
            onClose={handleCloseStockModal}
            product={selectedProduct}
            onLoadProduct={handleLoadProduct}
          />
        )}

        {/* Write-Down Modal */}
        {writeDownModalOpen && (
          <ProductWriteDownModal
            open={writeDownModalOpen}
            onClose={handleCloseWriteDownModal}
            product={selectedProduct}
          />
        )}

        {/* Delete Modal */}
        <ModalAlert
          open={showConfirmModal}
          setOpen={(open) => {
            setShowConfirmModal(open);
          }}
          title="products.modal.title.delete"
          description="products.modal.description.delete"
          descriptionNode={getDeleteDescription()}
          confirmButtonText="Yes"
          cancelButtonText="Cancel"
          loadingButton={deleteProductLoading}
          disabledButton={deleteProductLoading}
          onConfirm={onDelete}
          onCancel={() => {
            cancelAPIRequest(request);
            setShowConfirmModal(false);
          }}
        />

        {/* Error Modal */}
        {deleteProductError && (
          <ErrorDialog
            open={showErrorModal}
            error={deleteProductError}
            onClose={() => {
              setShowErrorModal(false);
              clearBusinessProductError(dispatch);
            }}
          />
        )}
      </main>
    </AccountingWrapper>
  );
};

export default ProductsServices;
